import {FormattedMessage} from '@pinecast/i18n';
import * as React from 'react';

import StatusMessage from '../StatusMessage';
import StatusToast from '../StatusToast';

type CopyResult = {copied: boolean; count: number; text: string};

type Options = {
  /**
   * Shows the message in a toast beside this element, instead of in place
   * (see `StatusToast`): for a control with no room around it, such as an
   * icon in a field's end cap. Pass it from the first render, while it is
   * still `null`. The status still has to be rendered, because it holds the
   * live regions.
   */
  toastAnchor?: Element | null;
};

/**
 * Copies `text` to the clipboard, and tells the user whether that worked.
 *
 * Returns the function that copies and the status to render. A copy changes
 * nothing on the page, so without the status nobody can tell that it happened,
 * and a screen reader user hears nothing at all. Put the status where the
 * message fits, such as beside a button, or pass `toastAnchor` when nothing
 * fits. It shows the message and announces it (see `StatusMessage`).
 *
 * Each copy writes its message into a new node, which is what a screen reader
 * announces, so a second "Copied" is announced like the first. The message
 * belongs to the text that was copied, and goes away when `text` changes.
 */
export function useCopyToClipboard(
  text: string,
  {toastAnchor}: Options = {},
): [() => Promise<void>, React.ReactElement] {
  const [result, setResult] = React.useState<CopyResult | null>(null);

  const copy = React.useCallback(async () => {
    let copied = true;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Refused by the browser, or no clipboard at all on an insecure page.
      copied = false;
    }
    setResult(previous => ({copied, count: (previous?.count ?? 0) + 1, text}));
  }, [text]);

  const current = result?.text === text ? result : null;
  const message = current && (
    <span key={current.count}>
      {current.copied ? (
        <FormattedMessage
          description="Status message after text is copied to the clipboard"
          defaultMessage="Copied"
        />
      ) : (
        <FormattedMessage
          description="Error message when the browser does not let the page copy text to the clipboard"
          defaultMessage="Could not copy. Select the text and copy it yourself."
        />
      )}
    </span>
  );
  const type = current ? (current.copied ? 'positive' : 'negative') : undefined;

  const status =
    toastAnchor === undefined ? (
      <StatusMessage message={message} type={type} />
    ) : (
      <StatusToast
        anchor={toastAnchor}
        message={message}
        messageKey={current?.count}
        type={type}
      />
    );
  return [copy, status];
}
